/**
 * Low-level xlsx READ helpers for the upload-validation entrance (entrance ①).
 *
 * Mirror image of `src/lib/merge/xlsx-zip.ts` (which WRITES cells): here we READ
 * cell values back out of an uploaded workbook. PURE module — browser + Node
 * safe, no `fs`/network. Built on jszip (already a dependency).
 *
 * Robustness: real BRITZMEDI forms store cell text as inline strings
 * (`t="inlineStr"`), but an arbitrary xlsx re-saved by Excel may instead use the
 * shared-string table (`t="s"`). We support BOTH, plus numbers and date serials.
 * Sheets are resolved BY NAME from the workbook (a re-saved file may reorder the
 * worksheet parts), falling back to a part path when a name is absent.
 */

import JSZip from "jszip";

/** A parsed workbook: sheet-name -> worksheet XML string + shared strings. */
export interface ParsedWorkbook {
  zip: JSZip;
  /** lower-cased sheet name -> worksheet XML part path. */
  sheetParts: Map<string, string>;
  /** Ordered worksheet part paths (sheet1.xml, sheet2.xml, ...). */
  orderedParts: string[];
  /** Shared string table (index -> text), empty when the file has none. */
  sharedStrings: string[];
}

/** Decode XML entities in a text node. */
function xmlUnescape(s: string): string {
  return s
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&#(\d+);/g, (_, d: string) => String.fromCodePoint(Number(d)))
    .replace(/&amp;/g, "&"); // last, so "&amp;lt;" -> "&lt;"
}

/** Concatenate every <t>…</t> text node inside an XML fragment. */
function collectText(fragment: string): string {
  const out: string[] = [];
  const re = /<t[^>]*>([\s\S]*?)<\/t>/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(fragment))) out.push(xmlUnescape(m[1] ?? ""));
  return out.join("");
}

/** Load shared strings (if present) into an index-addressable array. */
function parseSharedStrings(xml: string): string[] {
  const out: string[] = [];
  const re = /<si\b[^>]*>([\s\S]*?)<\/si>/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(xml))) out.push(collectText(m[1] ?? ""));
  return out;
}

/**
 * Map sheet display names to worksheet part paths via workbook.xml +
 * workbook.xml.rels. Falls back to ordinal sheetN.xml when rels are unusual.
 */
async function mapSheetParts(zip: JSZip): Promise<{
  byName: Map<string, string>;
  ordered: string[];
}> {
  const byName = new Map<string, string>();
  const ordered = Object.keys(zip.files)
    .filter((p) => /^xl\/worksheets\/sheet\d+\.xml$/.test(p))
    .sort((a, b) => {
      const na = Number(a.match(/sheet(\d+)\.xml/)?.[1] ?? 0);
      const nb = Number(b.match(/sheet(\d+)\.xml/)?.[1] ?? 0);
      return na - nb;
    });

  const wbFile = zip.file("xl/workbook.xml");
  const relsFile = zip.file("xl/_rels/workbook.xml.rels");
  if (wbFile && relsFile) {
    const wb = await wbFile.async("string");
    const rels = await relsFile.async("string");
    // rId -> target part
    const ridToTarget = new Map<string, string>();
    const relRe = /<Relationship\b[^>]*\bId="([^"]+)"[^>]*\bTarget="([^"]+)"[^>]*\/?>/g;
    let rm: RegExpExecArray | null;
    while ((rm = relRe.exec(rels))) {
      const id = rm[1] ?? "";
      let target = rm[2] ?? "";
      if (!target.startsWith("xl/")) target = `xl/${target.replace(/^\/?/, "")}`;
      ridToTarget.set(id, target);
    }
    // sheet name -> rId  (only real sheets carry a name + r:id)
    const sheetRe = /<sheet\b[^>]*\bname="([^"]+)"[^>]*\br:id="([^"]+)"[^>]*\/?>/g;
    let sm: RegExpExecArray | null;
    while ((sm = sheetRe.exec(wb))) {
      const name = xmlUnescape(sm[1] ?? "");
      const rid = sm[2] ?? "";
      const target = ridToTarget.get(rid);
      if (target && zip.file(target)) byName.set(name.toLowerCase(), target);
    }
  }
  return { byName, ordered };
}

/** Load + index an uploaded xlsx for reading. */
export async function loadWorkbook(
  bytes: ArrayBuffer | Uint8Array,
): Promise<ParsedWorkbook> {
  const zip = await JSZip.loadAsync(bytes);
  const { byName, ordered } = await mapSheetParts(zip);
  const sstFile = zip.file("xl/sharedStrings.xml");
  const sharedStrings = sstFile
    ? parseSharedStrings(await sstFile.async("string"))
    : [];
  return { zip, sheetParts: byName, orderedParts: ordered, sharedStrings };
}

/**
 * Resolve a worksheet part path by display name (case-insensitive). When the
 * name is missing (e.g. file re-saved with renamed tabs), fall back to the given
 * ordinal part path if it exists.
 */
export function resolveSheetPart(
  wb: ParsedWorkbook,
  name: string,
  fallbackPart: string,
): string | null {
  const byName = wb.sheetParts.get(name.toLowerCase());
  if (byName) return byName;
  if (wb.zip.file(fallbackPart)) return fallbackPart;
  return null;
}

/** A loaded worksheet: raw XML + the shared-string table. */
export interface SheetReader {
  xml: string;
  sharedStrings: string[];
}

export async function readSheet(
  wb: ParsedWorkbook,
  part: string,
): Promise<SheetReader> {
  const f = wb.zip.file(part);
  if (!f) throw new Error(`[xlsx-read] missing sheet part: ${part}`);
  return { xml: await f.async("string"), sharedStrings: wb.sharedStrings };
}

/**
 * Read a single cell value as a STRING (resolving inline strings, shared
 * strings, and numbers). Returns "" when the cell is absent/blank.
 */
export function readCellString(sheet: SheetReader, ref: string): string {
  const cell = matchCell(sheet.xml, ref);
  if (!cell) return "";
  // shared string reference: t="s" with <v>INDEX</v>
  if (/\bt="s"/.test(cell)) {
    const idx = Number(numberValue(cell));
    return Number.isFinite(idx) ? sheet.sharedStrings[idx] ?? "" : "";
  }
  // inline string: <is>…<t>…</t></is>
  if (/\bt="inlineStr"/.test(cell) || /<is>/.test(cell)) {
    const isMatch = cell.match(/<is>([\s\S]*?)<\/is>/);
    if (isMatch) return collectText(isMatch[1] ?? "");
  }
  // boolean / str / number — fall back to <v>
  const v = numberValue(cell);
  return v;
}

/**
 * Read a single cell as a NUMBER. Returns null when absent or non-numeric.
 * (Shared/inline string cells that happen to hold a numeric string are parsed.)
 */
export function readCellNumber(sheet: SheetReader, ref: string): number | null {
  const s = readCellString(sheet, ref);
  if (s.trim() === "") return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

/** Find a `<c r="REF" …>…</c>` (or self-closing) element for a cell ref. */
function matchCell(xml: string, ref: string): string | null {
  const re = new RegExp(`<c r="${ref}"[^>]*?(?:/>|>[\\s\\S]*?</c>)`);
  const m = xml.match(re);
  return m ? m[0] : null;
}

/** Pull the `<v>…</v>` text out of a cell element. */
function numberValue(cell: string): string {
  const m = cell.match(/<v>([\s\S]*?)<\/v>/);
  return m ? xmlUnescape(m[1] ?? "") : "";
}

/**
 * Convert an Excel 1900-system date serial back to YYYY-MM-DD.
 * Inverse of `dateToSerial` in src/lib/merge/xlsx-zip.ts (1899-12-30 baseline).
 */
export function serialToDate(serial: number): string {
  const epoch = Date.UTC(1899, 11, 30);
  const ms = epoch + Math.round(serial) * 86_400_000;
  return new Date(ms).toISOString().slice(0, 10);
}
